'use client';

import React, { useState, useRef, ReactNode } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority'
import { dequal } from 'dequal';

import type { DropdownOption, DropdownTemplate, DropdownEvent } from './types';
import { useOuterClick } from '@nf/utils-common/hooks';
import { DropdownItem } from './dropdown-item';

const dropdownVariants = cva('dropdown', {
	variants: {
		variant: {
			left: '',
			right: 'dropdown--right',
			full: 'dropdown--full'
		}
	},
	defaultVariants: {
		variant: 'left'
	}
});

export interface DropdownProps extends VariantProps<typeof dropdownVariants> {
	placeholder?: string | ReactNode;
	value?: any;
	options: DropdownOption[];
	className?: string;
	analyseKey?: string;
	isError?: boolean;
	optionTemplate?: DropdownTemplate;
	valueTemplate?: DropdownTemplate;
	frIconTemplate?: () => ReactNode;
	iconTemplate?: () => ReactNode;
	isActive?(option: DropdownOption): boolean;
	onChange?(event: DropdownEvent): void;
	onOpenToggled?(isOpen: boolean): void;
}

export const Dropdown = ({ 
	placeholder, 
	value, 
	options, 
	className,
	analyseKey,
	variant,
	isError,
	optionTemplate,
	valueTemplate,
	isActive,
	frIconTemplate,
	iconTemplate,
	onChange,
	onOpenToggled
}: DropdownProps) => {
	const [showMenu, setShowMenu] = useState(false);
	const selectRef = useRef<HTMLDivElement>(null);

	useOuterClick(selectRef, () => {
		setShowMenu(false);
		if (onOpenToggled) {
			onOpenToggled(false);
		}
	});

	const getSelectedOption = () => {
		return options.find(o => dequal(o.value, value));
	};

	const selectedOption = getSelectedOption();

	const isSelected = (option: DropdownOption) => dequal(option.value, value);

	const handleTriggerClick = () => {
		setTimeout(() => {
			// 在透過portal渲染的元件useOuterClick會在打開dropdown 後立即觸發, 設置setTimeout避免此狀況
			const isShow = !showMenu;
			setShowMenu(isShow);
			if (onOpenToggled) {
				onOpenToggled(isShow);
			}
		}, 0)
	};

	const createTriggerLabel = () => {
		if (selectedOption && valueTemplate) {
			return valueTemplate(selectedOption);
		}

		const label = selectedOption?.label;
		return label || placeholder || '';
	};

	const createIsActive = (): boolean => {
		if (selectedOption && isActive) {
			return isActive(selectedOption);
		}
		return Boolean(selectedOption);
	}

	return (
		<div 
			className={dropdownVariants({ variant, className })}
			data-open={showMenu}
			data-active={createIsActive()}
			data-error={isError}
			data-focus={showMenu}
		>
			<div 
				className="dropdown__btn"
				onClick={handleTriggerClick}
			>
				{frIconTemplate && frIconTemplate()}
				<div className="dropdown__current">
					{createTriggerLabel()}
				</div>
				{iconTemplate && iconTemplate()}
			</div>

			{showMenu ? (
				<div 
					className="dropdown__container scrollbar" 
				>
					<div 
						ref={selectRef} 
						className="dropdown__content" //需要利用此元素推margin讓父層擴展推出與scrollbar的間距
					>
						<div className="scrollbar">
							{options.map(option => (
								<DropdownItem 
									key={option.label}
									option={option}
									isSelected={isSelected(option)}
									template={optionTemplate}
									analyseKey={analyseKey}
									setShowMenu={setShowMenu}
									onChange={onChange}
									onOpenToggled={onOpenToggled}
								/>
							))}
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
};

